import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import axios from 'axios';
import { showErrorThunk } from '@spike/notifications-action';
import { RootState } from '../../../store';
import { fetchLocationDuplication, startLocationDuplication } from '../../../actions/location/LocationActions';
import {
    DuplicateLocationDetails,
    Location,
    LocationCopyArea,
    LocationDuplication,
    LOCATION_COPY_AREAS
} from '../../../model/location';
import {
    DuplicateDetailsField,
    DuplicateFieldErrors,
    toDuplicateFieldErrors,
    validateDuplicateDetails
} from './duplicateLocationValidation';

const HTTP_STATUS_403_FORBIDDEN = 403;
const HTTP_STATUS_422_UNPROCESSABLE_ENTITY = 422;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const FORBIDDEN_MESSAGE = "You don't have access to duplicate this location.";
const GENERIC_ERROR_MESSAGE = 'Unable to duplicate this location. Please try again.';

const ALL_AREAS: Array<LocationCopyArea> = LOCATION_COPY_AREAS.map(area => area.id);

export type DuplicateStep = 'details' | 'checklist' | 'submitting' | 'running' | 'stalled' | 'completed' | 'failed';

export interface DuplicateLocationState {
    step: DuplicateStep;
    details: DuplicateLocationDetails;
    areas: Array<LocationCopyArea>;
    fieldErrors: DuplicateFieldErrors;
    generalError?: string;
    result: LocationDuplication | null;
    updateDetail: (field: DuplicateDetailsField, value: string) => void;
    toggleArea: (area: LocationCopyArea) => void;
    toggleAll: () => void;
    goToChecklist: () => void;
    goBack: () => void;
    submit: () => Promise<void>;
    retry: () => void;
    reset: () => void;
}

const emptyDetails: DuplicateLocationDetails = {
    businessName: '',
    email: '',
    phone: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zipcode: '',
    country: ''
};

const toDetails = (location: Location | null): DuplicateLocationDetails => {
    if (!location) return emptyDetails;

    const address = location.addressesAttributes?.[0];

    return {
        businessName: location.businessName,
        email: location.email ?? '',
        phone: location.phone ?? '',
        addressLineOne: address?.addressLineOne ?? '',
        addressLineTwo: address?.addressLineTwo ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        zipcode: address?.zipcode ?? '',
        country: address?.country ?? ''
    };
};

/**
 * State of the duplicate location flow: details form, checklist of areas to copy,
 * starting the duplication and polling its status until it completes or fails.
 */
export const useDuplicateLocation = (location: Location | null): DuplicateLocationState => {
    // The notifications thunk never reads the store, so it is dispatched without tying it to this app's state type
    const dispatch = useDispatch<ThunkDispatch<never, null, AnyAction>>();
    const organizations = useSelector((state: RootState) => state.organizations.organizations);

    // Same country the add location form uses to validate phone numbers
    const parentOrganization = organizations.find(
        org => org.parentOrganizationId === null || org.parentOrganizationId === undefined
    );
    const phoneCountry = parentOrganization?.country || 'US';

    const [step, setStep] = useState<DuplicateStep>('details');
    const [details, setDetails] = useState<DuplicateLocationDetails>(() => toDetails(location));
    const [areas, setAreas] = useState<Array<LocationCopyArea>>(ALL_AREAS);
    const [fieldErrors, setFieldErrors] = useState<DuplicateFieldErrors>({});
    const [generalError, setGeneralError] = useState<string | undefined>();
    const [result, setResult] = useState<LocationDuplication | null>(null);

    const isSubmittingRef = useRef(false);
    const pollTimerRef = useRef<number | null>(null);
    // Incremented to invalidate responses of a polling run that was stopped
    const pollRunRef = useRef(0);

    const stopPolling = useCallback(() => {
        pollRunRef.current += 1;
        if (pollTimerRef.current !== null) {
            window.clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    const pollStatus = useCallback(
        (duplicationId: string, run: number, startedAt: number, consecutiveErrors: number) => {
            pollTimerRef.current = window.setTimeout(async () => {
                pollTimerRef.current = null;
                let errors = consecutiveErrors;

                try {
                    const duplication = await fetchLocationDuplication(duplicationId);
                    if (run !== pollRunRef.current) return;

                    if (duplication.status === 'completed' || duplication.status === 'failed') {
                        setResult(duplication);
                        setStep(duplication.status);
                        return;
                    }
                    errors = 0;
                } catch {
                    if (run !== pollRunRef.current) return;
                    errors += 1;
                }

                if (errors >= MAX_CONSECUTIVE_POLL_ERRORS || Date.now() - startedAt >= POLL_TIMEOUT_MS) {
                    setStep('stalled');
                    return;
                }

                pollStatus(duplicationId, run, startedAt, errors);
            }, POLL_INTERVAL_MS);
        },
        []
    );

    const updateDetail = useCallback((field: DuplicateDetailsField, value: string) => {
        setDetails(prev => ({ ...prev, [field]: value }));
        setFieldErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    }, []);

    const toggleArea = useCallback((area: LocationCopyArea) => {
        setAreas(prev =>
            prev.includes(area)
                ? prev.filter(item => item !== area)
                : ALL_AREAS.filter(item => item === area || prev.includes(item))
        );
    }, []);

    const toggleAll = useCallback(() => {
        setAreas(prev => (prev.length === ALL_AREAS.length ? [] : ALL_AREAS));
    }, []);

    const goToChecklist = useCallback(() => {
        const errors = validateDuplicateDetails(details, location?.businessName ?? '', phoneCountry);
        setFieldErrors(errors);

        if (Object.keys(errors).length === 0) {
            setGeneralError(undefined);
            setStep('checklist');
        }
    }, [details, location?.businessName, phoneCountry]);

    const goBack = useCallback(() => setStep('details'), []);

    const submit = useCallback(async () => {
        if (!location || isSubmittingRef.current || areas.length === 0) return;

        isSubmittingRef.current = true;
        setGeneralError(undefined);
        setStep('submitting');

        // Closing the modal while the request is in flight invalidates this run, so polling never starts after it
        const run = pollRunRef.current;

        try {
            const duplicationId = await startLocationDuplication(location.id, details, areas);
            if (run !== pollRunRef.current) return;

            setStep('running');
            pollStatus(duplicationId, run, Date.now(), 0);
        } catch (apiError: unknown) {
            if (run !== pollRunRef.current) return;

            const status = axios.isAxiosError(apiError) ? apiError.response?.status : undefined;

            if (status === HTTP_STATUS_422_UNPROCESSABLE_ENTITY) {
                const { fieldErrors: apiFieldErrors, generalError: apiGeneralError } = toDuplicateFieldErrors(apiError);
                setFieldErrors(apiFieldErrors);
                setGeneralError(apiGeneralError);
                setStep('details');
            } else {
                // The backend answers 403 (not 401) for locations outside the organization, so the user stays logged in
                dispatch(
                    showErrorThunk(status === HTTP_STATUS_403_FORBIDDEN ? FORBIDDEN_MESSAGE : GENERIC_ERROR_MESSAGE)
                );
                setStep('checklist');
            }
        } finally {
            isSubmittingRef.current = false;
        }
    }, [location, areas, details, dispatch, pollStatus]);

    const retry = useCallback(() => {
        setResult(null);
        setGeneralError(undefined);
        setStep('details');
    }, []);

    const reset = useCallback(() => {
        stopPolling();
        setStep('details');
        setDetails(toDetails(location));
        setAreas(ALL_AREAS);
        setFieldErrors({});
        setGeneralError(undefined);
        setResult(null);
    }, [location, stopPolling]);

    return {
        step,
        details,
        areas,
        fieldErrors,
        generalError,
        result,
        updateDetail,
        toggleArea,
        toggleAll,
        goToChecklist,
        goBack,
        submit,
        retry,
        reset
    };
};

export default useDuplicateLocation;
